// Utilities for cleaning Google-derived address strings for editorial display.
//
// `addressDetails.colonia` and friends come from Google Geocoding and are not
// reliable place names: they can be a Plus Code prefix
// ("MM8M+HC Monterrey, Nuevo Leon"), a placeholder ("Sin Nombre de Colonia 27"),
// or a comma-laden raw address. UI surfaces that want a clean human place name
// (H1 italic accents, similar-listing headlines) should call
// `cleanAccentLocation` and fall back when it returns null.

const PLUS_CODE_PREFIX_RE = /^[A-Z0-9]{4,}\+[A-Z0-9]*[\s,]*/i
const PLUS_CODE_FRAGMENT_RE = /[A-Z0-9]{4,}\+[A-Z0-9]/i
const PLACEHOLDER_NAME_RE = /^sin\s+nombre/i

/**
 * Returns a clean human-readable place name, or null if the input is a
 * Plus Code, a "Sin Nombre" placeholder, or otherwise unusable.
 *
 * - Strips a leading Plus Code prefix.
 * - Rejects values where a Plus Code remains anywhere.
 * - Rejects "Sin Nombre de Colonia N" placeholders.
 * - Collapses comma-laden raw addresses to the first segment
 *   ("Monterrey, Nuevo Leon" → "Monterrey").
 * - Returns null for too-short or empty values.
 */
export function cleanAccentLocation(
  value: string | null | undefined
): string | null {
  if (!value) return null
  let cleaned = value.trim()
  if (PLUS_CODE_PREFIX_RE.test(cleaned)) {
    cleaned = cleaned.replace(PLUS_CODE_PREFIX_RE, '').trim()
  }
  if (PLUS_CODE_FRAGMENT_RE.test(cleaned)) return null
  if (PLACEHOLDER_NAME_RE.test(cleaned)) return null
  const firstSegment = cleaned.split(',')[0]?.trim() ?? ''
  return firstSegment.length >= 3 ? firstSegment : null
}
